import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import InfoText from '../atoms/InfoText';
import TitleText from '../atoms/TitleText';
import Avatar from '../atoms/Avatar';
import Chip from '../atoms/Chip';
import KeepReview from './Modals/KeepReview';
import DeleteFeedback from './Modals/DeleteFeedback';
import classNames from 'classnames';
import { CloseCircle, TickCircle, ArrowDown2, ArrowUp2 } from 'iconsax-react';
import { Collapse } from 'antd';
import { format } from 'date-fns';
// import watchImage from '../../assets/images/orange_smartwatch.png';
import ReviewCard from './ReviewCard';

export type ProfileType = {
  name: string;
  url: string;
  type: string;
};

export type BuyerAttachment = {
  id: string;
  name: string;
  blobFileUrl: string;
  extension: string;
  type: string;
};

export type ReviewItem = {
  id: string;
  requesterProfile: ProfileType;
  receiverProfile: ProfileType;
  requesterFeedbackNotes: string | null;
  requesterFeedbackReason: string | null;
  requesterShipmentReason: string | null;
  requesterFeedbackAttachments?: any[];
  buyerAttachments?: BuyerAttachment[];
  attachments?: any[];
  requesterReportComments: string | null;
  operatorComments: string | null;
  rating: number | null;
  status: {
    code: string;
    name: string;
    submittedDate: string;
  };
  actions: {
    canKeep: boolean;
    canDelete: boolean;
  };
};

export default function ReviewBox({
  review,
  refetch,
  refetchCallBack,
}: {
  review: ReviewItem;
  refetch?: () => Promise<{
    data?: unknown;
    error?: unknown;
  }>;
  refetchCallBack: () => void;
}) {
  const { t } = useTranslation();
  const [openedModal, setModalOpen] = useState<string | null>(null);
  const handleTriggerModal = (id: string | null) => setModalOpen(id);

  const getProfileTypeTranslation = (type: string) => {
    if (type === 'Business Seller') return t('home.businessSeller');
    if (type === 'Business Buyer') return t('home.businessBuyer');
    return type;
  };

  const formattedDate = review.status?.submittedDate
    ? format(new Date(review.status.submittedDate), 'MMM dd, yyyy')
    : '';

  const isPending = review.status?.code === 'PENDING';

  return (
    <div
      className="bg-white dark:bg-gray-900 border border-[#E9EBEF] dark:border-gray-800 rounded-lg p-6 shadow-sm transition-all"
      data-testid="review-box-container"
    >
      <Collapse
        ghost
        expandIconPosition="end"
        expandIcon={({ isActive }) => (
          isActive ? (
            <ArrowUp2 size={20} className="text-[#090909] dark:text-dark-text-primary" data-testid="review-collapse-arrow-up" />
          ) : (
            <ArrowDown2 size={20} className="text-[#090909] dark:text-dark-text-primary" data-testid="review-collapse-arrow-down" />
          )
        )}
        className="w-full [&>.ant-collapse-item>.ant-collapse-header]:!p-0 [&>.ant-collapse-item>.ant-collapse-header]:!items-start [&>.ant-collapse-item>.ant-collapse-content>.ant-collapse-content-box]:!p-0 [&>.ant-collapse-item]:!border-b-0 [&_.ant-collapse-expand-icon]:!mt-[14px]"
        items={[
          {
            key: '1',
            label: (
              <div className="flex flex-col gap-2 w-full pr-1">
                {/* --- Header (Clickable row) --- */}
                <div
                  className="flex items-center justify-between w-full"
                  data-testid="review-box-header"
                >
                  <Avatar
                    image={review.receiverProfile?.url || ''}
                    title={review.receiverProfile?.name || '---'}
                    subTitle={
                      <InfoText
                        text={t('reviews.submittedAtWithRole', {
                          role: getProfileTypeTranslation(review.receiverProfile?.type),
                          date: formattedDate,
                        })}
                        className="!text-[#777777] dark:!text-dark-text-secondary !leading-[100%] !text-sm"
                      />
                    }
                    withoutBorder={false}
                    imageSize={48}
                    className="!flex-row !items-center gap-3"
                    data-testid="review-receiver-avatar"
                  />

                  <div className="flex items-center gap-3" data-testid="review-header-badge-collapse">
                    <Chip
                      type={isPending ? 'warning' : 'success'}
                      className={classNames(
                        '!rounded-full !px-4 !py-2 !text-sm',
                        isPending && '!bg-[#FEF6F2] dark:!bg-orange-900/30 dark:!text-orange-400'
                      )}
                      data-testid="review-status-badge"
                    >
                      {isPending
                        ? t('reviews.newBadge')
                        : t('reviews.resolved')}
                    </Chip>
                  </div>
                </div>

                {/* --- Collapsed / Always Visible Report Summary --- */}
                <div className="flex flex-wrap items-baseline gap-x-1" data-testid="review-report-summary">
                  <InfoText
                    text={`${t('reviews.sellerNotesLabel')} : `}
                    className="!font-bold !text-[#090909] dark:!text-dark-text-primary !text-base"
                    maxLength="FULL"
                    data-testid="review-notes-label"
                  />
                  <InfoText
                    text={review.requesterReportComments || '---'}
                    className="!text-[#090909] dark:!text-dark-text-secondary !text-base"
                    maxLength="FULL"
                    data-testid="review-notes-value"
                  />
                </div>
              </div>
            ),
            children: (
              <div className="flex flex-col pt-1" data-testid="review-expanded-content">
                {/* Grey "Review details" box */}
                <ReviewCard
                  title={t('reviews.reviewDetails')}
                  reviewerImage={review.requesterProfile?.url || ''}
                  reviewerName={review.requesterProfile?.name || '---'}
                  reviewerRoleAndDate={t('reviews.submittedAtWithRole', {
                    role: getProfileTypeTranslation(review.requesterProfile?.type),
                    date: formattedDate,
                  })}
                  rating={review.rating}
                  notes={
                    <div className="flex flex-col gap-1" data-testid="buyer-notes-container">
                      <div className="flex flex-wrap items-baseline gap-x-1">
                        <InfoText
                          text={`${t('reviews.buyerNotesLabel')} : `}
                          className="!font-bold !text-[#090909] dark:!text-dark-text-primary !text-base"
                          maxLength="FULL"
                          data-testid="buyer-notes-label"
                        />
                        <InfoText
                          text={review.requesterFeedbackNotes || '---'}
                          className="!text-[#090909] dark:!text-dark-text-secondary !text-base !leading-[100%]"
                          maxLength={250}
                          data-testid="buyer-notes-value"
                        />
                      </div>
                      {(review.requesterFeedbackReason) && (
                        <div className="flex flex-wrap items-baseline gap-x-1">
                          <InfoText
                            text={`${t('reviews.reportReason')} : `}
                            className="!font-bold !text-[#090909] dark:!text-dark-text-primary !text-base"
                            maxLength="FULL"
                            data-testid="buyer-notes-reason-label"
                          />
                          <InfoText
                            text={`${review.requesterFeedbackReason}`}
                            className="!text-[#090909] dark:!text-dark-text-secondary !text-base"
                            maxLength="FULL"
                            data-testid="buyer-notes-reason-value"
                          />
                        </div>
                      )}
                    </div>
                  }
                  attachments={
                    review.buyerAttachments && review.buyerAttachments.length > 0 ? (
                      <>
                        {review.buyerAttachments.map((att, index) => {
                          const url = att.blobFileUrl;
                          if (!url) return null;
                          return (
                            <div
                              key={index}
                              className="w-[56px] h-[56px] min-w-[56px] min-h-[56px] rounded-[12px] overflow-hidden border border-[#D9D9D9] dark:border-dark-surface-border bg-white"
                              data-testid={`attachment-image-${index + 1}`}
                            >
                              <img
                                src={url}
                                alt={`attachment-${index + 1}`}
                                className="w-full h-full object-cover"
                              />
                            </div>
                          );
                        })}
                      </>
                    ) : undefined
                  }
                />
{/* alsdjfsa */}
                {/* --- Admin Decision (if resolved) --- */}
                {!isPending && review.operatorComments && (
                  <div className="flex flex-col gap-2 mt-2" data-testid="admin-decision-section">
                    <TitleText
                      text={t('reviews.adminDecision')}
                      className="!font-bold !text-base !text-[#090909] dark:!text-dark-text-primary"
                      data-testid="admin-decision-title"
                    />
                    <div className="flex flex-wrap items-baseline gap-x-1 mt-1" data-testid="admin-decision-details">
                      <InfoText
                        text={`${review.status.code === 'REJECTED'
                          ? t('reviews.feedbackDeleted')
                          : t('reviews.feedbackKept')
                          } `}
                        className={classNames(
                          '!font-bold !text-base',
                          review.status.code === 'REJECTED'
                            ? '!text-[#CC2936]' // Red
                            : '!text-[#4DA167]' // Green
                        )}
                        maxLength="FULL"
                        data-testid="admin-decision-status-label"
                      />
                      <InfoText
                        text={`${t('reviews.notesLabel')}: `}
                        className="!font-bold !text-[#777777] dark:!text-dark-text-secondary !text-base"
                        maxLength="FULL"
                        data-testid="admin-decision-notes-label"
                      />
                      <InfoText
                        text={review.operatorComments}
                        className="!text-[#777777] dark:!text-dark-text-secondary !text-base"
                        maxLength="FULL"
                        data-testid="admin-decision-comments"
                      />
                    </div>
                  </div>
                )}

                {/* --- Action Buttons (if pending) --- */}
                {isPending && (
                  <div className="flex justify-end gap-3 pt-2" data-testid="review-actions-row">
                    <button
                      type="button"
                      onClick={() => handleTriggerModal(`KEEP-${review.id}`)}
                      className="px-4 py-2 rounded-[44px] flex items-center gap-1.5 bg-[#F3F6FD] dark:bg-[#1E293B] !text-[#3C91E6] hover:opacity-85 transition-opacity !font-medium !text-sm"
                      data-testid="keep-feedback-btn"
                    >
                      <TickCircle size={18} data-testid="keep-icon" />
                      {t('reviews.keepFeedback')}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleTriggerModal(`DELETE-${review.id}`)}
                      className="px-4 py-2 rounded-[44px] flex items-center gap-1.5 bg-[#FDF4F6] dark:bg-[#3B1E1E] !text-[#CC2936] hover:opacity-85 transition-opacity !font-medium !text-sm"
                      data-testid="delete-feedback-btn"
                    >
                      <CloseCircle size={18} data-testid="delete-icon" />
                      {t('reviews.deleteFeedback')}
                    </button>
                  </div>
                )}
              </div>
            ),
          },
        ]}
      />

      <KeepReview
        isModalOpen={openedModal === `KEEP-${review.id}`}
        handleCloseModal={() => handleTriggerModal(null)}
        refetch={
          refetch as () => Promise<{
            data?: unknown;
            error?: unknown;
          }>
        }
        reviewId={review.id}
        refetchCallBack={refetchCallBack}
      />

      <DeleteFeedback
        isModalOpen={openedModal === `DELETE-${review.id}`}
        handleCloseModal={() => handleTriggerModal(null)}
        reviewId={review.id}
        refetch={
          refetch as () => Promise<{
            data?: unknown;
            error?: unknown;
          }>
        }
        refetchCallBack={refetchCallBack}
      />
    </div>
  );
}
